import { ValidatorFn } from '@angular/forms';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';

export interface ValidatorFieldTypeMap {
  validator: ValidatorType,
  hasDefinition: boolean,
  definitionValidators?: ValidatorFn[],
  definitionPlaceholder?: string
}

/**
 * Maps a field type to the validators it supports
 * @param fieldType the fieldType selected for which we want the validators
 * @param entityType Entity Type related to the field
 * @param noAssetTypes if the field is unrelated to assetTypes
 * @return a list of validator types and whether they require a definition
 */
export default function validatorFieldTypeMapping(fieldType: FieldTypeEnum, entityType: EntityTypeEnum, noAssetTypes: boolean): ValidatorFieldTypeMap[] {
  const validators: ValidatorFieldTypeMap[] = [];

  // Only entity types with creation popup sections can create a field with the required validator.
  // A field can't be required and related to an Asset Types, because the creation popup sections are not related to Asset Types
  if ([EntityTypeEnum.ASSET, EntityTypeEnum.CONTRACT, EntityTypeEnum.EQUIPMENT, EntityTypeEnum.PROJECT,
    EntityTypeEnum.SPACE, EntityTypeEnum.WORK].includes(entityType) && noAssetTypes) {
    validators.push({validator: ValidatorType.REQUIRED, hasDefinition: false});
  }

  if ([FieldTypeEnum.TEXT, FieldTypeEnum.TEXTAREA,
    FieldTypeEnum.TEXT_AUTOCOMPLETE, FieldTypeEnum.SUGGESTBOX,
    FieldTypeEnum.CHIPS, FieldTypeEnum.CHIPS_AUTOCOMPLETE].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.MAX_LENGTH, hasDefinition: true, definitionValidators: [ExtraValidators.integer]},
      {validator: ValidatorType.UNIQUE, hasDefinition: false}
    );
  }
  if ([FieldTypeEnum.NUMERIC].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.MIN_VALUE, hasDefinition: true, definitionValidators: [ExtraValidators.number]},
      {validator: ValidatorType.MAX_VALUE, hasDefinition: true, definitionValidators: [ExtraValidators.number]},
      {validator: ValidatorType.INTEGER, hasDefinition: false}
    );
  }
  if ([FieldTypeEnum.CHIPS, FieldTypeEnum.CHIPS_AUTOCOMPLETE, FieldTypeEnum.LIST_MULTIVALUE].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.DISTINCT, hasDefinition: false}
    );
  }
  if ([FieldTypeEnum.DATE, FieldTypeEnum.DATETIME].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.MAX_NOW, hasDefinition: false},
      {validator: ValidatorType.BEFORE_DATE, hasDefinition: true, definitionValidators: [ExtraValidators.dateISO], definitionPlaceholder: 'LABEL.REQUIRED_DATE_FORMAT'},
      {validator: ValidatorType.AFTER_DATE, hasDefinition: true, definitionValidators: [ExtraValidators.dateISO], definitionPlaceholder: 'LABEL.REQUIRED_DATE_FORMAT'}
    );
  }
  if ([FieldTypeEnum.YEAR].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.MIN_VALUE, hasDefinition: true, definitionValidators: [ExtraValidators.integer]},
      {validator: ValidatorType.MAX_VALUE, hasDefinition: true, definitionValidators: [ExtraValidators.integer]}
    );
  }
  if ([FieldTypeEnum.RADIOBUTTON].includes(fieldType)) {
    validators.push();
  }
  if ([FieldTypeEnum.LINK, FieldTypeEnum.EMAIL].includes(fieldType)) {
    validators.push(
      {validator: ValidatorType.REGEX, hasDefinition: true} // How to implement visually?
      /*
      TODO V2 - TTT-3417
      string contains, startsWith or endsWith definition?
       */
    );
  }

  /*
  TODO V2 - TTT-3417
  Some validators reference other fields and will be added to their corresponding lists in V2
  - requiredTrue
  - requiredFalse
  - equalTo
  - notEqualTo
  - afterDateField,
  - beforeDateField
  - gteField
  - lteField
   */

  return validators;
};
